/**
 * Review queue banner + dialog — the human side of manual message review.
 *
 * Lists AI drafts waiting on the user (held by their review mode, or AI
 * replies that need a manual push after a failure or the turn cap) and lets
 * them approve, edit then approve, or discard each one. Backed by
 * /api/inbox/approvals (campaigns worker).
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { ClipboardList, CheckCircle, Pencil, SkipForward, Trash2, Loader2, Wand2 } from 'lucide-react'
import {
  useApprovals,
  useApproveMessage,
  useRejectMessage,
  useReviseMessage,
  getMessageTypeLabel,
  type ApprovalItem,
  type MessageType,
} from './use-inbox'

interface ReviewQueueProps {
  className?: string
}

function typeLabel(messageType: string): string {
  if (messageType === 'auto_reply') return 'AI Reply'
  if (messageType === 'dm') return 'First DM'
  if (messageType === 'follow_up_3') return 'Follow-up 3'
  if (messageType === 'engagement_dm') return 'Engagement DM'
  return getMessageTypeLabel(messageType as MessageType) ?? messageType
}

export function ReviewQueue({ className }: ReviewQueueProps) {
  const { data: pending = [] } = useApprovals()
  const approveMutation = useApproveMessage()
  const rejectMutation = useRejectMessage()
  const reviseMutation = useReviseMessage()

  const [isOpen, setIsOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [editDraft, setEditDraft] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [wasRevised, setWasRevised] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const count = pending.length
  if (count === 0) return null

  const safeIndex = Math.min(currentIndex, count - 1)
  const current: ApprovalItem | undefined = pending[safeIndex]
  const busy = approveMutation.isPending || rejectMutation.isPending || reviseMutation.isPending

  function resetItemState() {
    setIsEditing(false)
    setEditDraft(null)
    setFeedbackOpen(false)
    setFeedback('')
    setWasRevised(false)
    setActionError(null)
  }

  function handleRevise() {
    if (!current || busy || feedback.trim().length < 3) return
    setActionError(null)
    reviseMutation.mutate(
      { id: current.id, feedback: feedback.trim() },
      {
        onSuccess: (revisedContent) => {
          // Show the revision immediately; the server row is already updated,
          // so approving without further edits sends exactly this text.
          setEditDraft(revisedContent)
          setIsEditing(false)
          setFeedback('')
          setFeedbackOpen(false)
          setWasRevised(true)
        },
        onError: (err) => {
          setActionError(err instanceof Error ? err.message : 'Revision failed')
        },
      },
    )
  }

  function handleApprove() {
    if (!current || busy) return
    setActionError(null)
    const edited = isEditing && editDraft !== null && editDraft.trim() !== current.content
    approveMutation.mutate(
      { id: current.id, ...(edited ? { content: editDraft!.trim() } : {}) },
      {
        onSuccess: () => {
          resetItemState()
          setCurrentIndex(0)
          if (count <= 1) setIsOpen(false)
        },
        onError: (err) => {
          setActionError(err instanceof Error ? err.message : 'Send failed')
        },
      },
    )
  }

  function handleReject() {
    if (!current || busy) return
    setActionError(null)
    rejectMutation.mutate(current.id, {
      onSuccess: () => {
        resetItemState()
        setCurrentIndex(0)
        if (count <= 1) setIsOpen(false)
      },
      onError: (err) => {
        setActionError(err instanceof Error ? err.message : 'Discard failed')
      },
    })
  }

  function handleSkip() {
    if (count <= 1) return
    setCurrentIndex((safeIndex + 1) % count)
    resetItemState()
  }

  function handleEditToggle() {
    if (!current) return
    if (!isEditing) {
      setEditDraft(current.content)
      setIsEditing(true)
    } else {
      setIsEditing(false)
    }
  }

  function handleOpen() {
    setCurrentIndex(0)
    resetItemState()
    setIsOpen(true)
  }

  return (
    <>
      {/* Banner */}
      <div
        className={`flex items-center justify-between border-b bg-[rgba(28,63,255,0.08)] px-4 py-2 ${className ?? ''}`}
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-[var(--color-cobalt-bright)]" />
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {count} message{count !== 1 ? 's' : ''} waiting for your review
          </span>
          <Badge className="bg-blue-600 text-[10px] text-white hover:bg-blue-600">
            {count}
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 border-[rgba(28,63,255,0.3)] text-xs text-[var(--color-cobalt-bright)] hover:bg-[rgba(28,63,255,0.12)]"
          onClick={handleOpen}
        >
          Review Now
        </Button>
      </div>

      {/* Review dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Review AI Draft</DialogTitle>
              <span className="text-xs text-muted-foreground">
                {safeIndex + 1} / {count}
              </span>
            </div>
          </DialogHeader>

          {current && (
            <div className="space-y-4">
              {/* Lead info */}
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {current.lead_name
                    .split(' ')
                    .slice(0, 2)
                    .map((p) => p[0])
                    .join('')}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{current.lead_name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[current.lead_title, current.lead_company].filter(Boolean).join(' at ') || current.campaign_name || ''}
                  </p>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                  {typeof current.qg_score === 'number' && (
                    <Badge variant="outline" className="text-[10px]" title="Quality gate score out of 10">
                      Quality {current.qg_score.toFixed(1)}/10
                    </Badge>
                  )}
                  <Badge variant="secondary" className="text-[10px]">
                    {typeLabel(current.message_type)}
                  </Badge>
                </div>
              </div>

              {current.hold_reason === 'stuck' && (
                <p className="text-xs text-amber-500">
                  This AI reply could not send automatically and needs your push.
                </p>
              )}
              {current.qg_feedback && (
                <p className="text-xs text-muted-foreground">
                  Quality check notes: {current.qg_feedback}
                </p>
              )}

              {/* Draft */}
              <div className="rounded-lg border bg-muted/30 p-3">
                {isEditing ? (
                  <textarea
                    className="w-full resize-none bg-transparent text-sm outline-none"
                    rows={6}
                    maxLength={3000}
                    value={editDraft ?? current.content}
                    onChange={(e) => setEditDraft(e.target.value)}
                    autoFocus
                  />
                ) : (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {editDraft ?? current.content}
                  </p>
                )}
              </div>

              {wasRevised && !reviseMutation.isPending && (
                <p className="text-xs text-[var(--color-cobalt-bright)]">
                  Revised with your feedback. Approve to send, or ask for another pass.
                </p>
              )}

              {/* Ask-AI feedback panel */}
              {feedbackOpen && (
                <div className="space-y-2 rounded-lg border border-dashed p-3">
                  <textarea
                    className="w-full resize-none bg-transparent text-sm outline-none"
                    rows={2}
                    maxLength={1000}
                    placeholder="Tell the AI what to change, e.g. shorter, mention their podcast, softer ask"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFeedbackOpen(false)
                        setFeedback('')
                      }}
                      disabled={reviseMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1.5"
                      onClick={handleRevise}
                      disabled={busy || feedback.trim().length < 3}
                    >
                      {reviseMutation.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Wand2 className="h-3.5 w-3.5" />
                      )}
                      Revise
                    </Button>
                  </div>
                </div>
              )}

              {actionError && <p className="text-xs text-destructive">{actionError}</p>}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button className="flex-1 gap-1.5" size="sm" onClick={handleApprove} disabled={busy}>
                  {approveMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle className="h-3.5 w-3.5" />
                  )}
                  Approve &amp; Send
                </Button>
                <Button
                  variant={feedbackOpen ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1 gap-1.5"
                  onClick={() => setFeedbackOpen((v) => !v)}
                  disabled={busy}
                  title="Tell the AI what to change and it rewrites the draft for you"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  Ask AI
                </Button>
                <Button
                  variant={isEditing ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1 gap-1.5"
                  onClick={handleEditToggle}
                  disabled={busy}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {isEditing ? 'Done Editing' : 'Edit'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-destructive hover:text-destructive"
                  onClick={handleReject}
                  disabled={busy}
                  title="Discard this draft. For outreach drafts the lead pauses for a day before a new draft is written."
                >
                  {rejectMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                  Discard
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5"
                  onClick={handleSkip}
                  disabled={busy || count <= 1}
                >
                  <SkipForward className="h-3.5 w-3.5" />
                  Skip
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
